import { faSquareCaretDown } from "@fortawesome/pro-solid-svg-icons/faSquareCaretDown";
import { faSquareCaretUp } from "@fortawesome/pro-solid-svg-icons/faSquareCaretUp";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Collapse, Grid, IconButton, Stack, Typography } from '@mui/material';
import { MprcButton, MprcContainer, MprcJoinComponents, MprcText, MprcTypography } from 'components';
import { MprcButtonProps } from "components/MprcButton";
import { EnrollStatus, TrainingComponentType } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityHeaderContent } from "views/lms/student/EnrollmentView/Course/ContentComponents/activity/ActivityHeaderContent";
import { useCourseEnrollmentViewContext } from "views/lms/student/EnrollmentView/Course/CourseEnrollmentView";
import { useEnrollmentViewContext } from "..";
import { DoCloseEnrollment } from "../Shared/DoCloseEnrollment";
import { getEnrollmentCertificate, ReEnrollAction } from "../Shared/EnrollmentActions";
import { EnrollmentCloseButton } from "../Shared/EnrollmentCloseButton";
import { EnrollmentStatus } from "../Shared/EnrollmentStatus";
import { ProgressResult } from "../Shared/ProgressResult";
import { ScoreResult } from "../Shared/ScoreResult";
import { TrainingAvatar } from "../Shared/TrainingAvatar";
import { TrainingDueDate } from "../Shared/TrainingDueDate";
import { TrainingInteractions } from "../Shared/TrainingInteractions";

/////////////////////////////////////////////////////////////////////////////////////

export const CourseEnrollmentViewHeader: React.FC = () => {

    const { t } = useTranslation("lms");

    const { enrollment, loading, onReload } = useEnrollmentViewContext();
    const { hasNext, onNextModule } = useCourseEnrollmentViewContext();

    const [dialog, setDialog] = useState<any>();
    const [closing, setClosing] = useState<boolean>(false);

    const [expanded, setExpanded] = useState<boolean>(enrollment.finalizationDate != null);

    /////////////////////////////////////////////////////////////////////////////////////

    ///
    const RenderActions = useMemo(() => {

        var actions: React.ReactNode[] = [];
        var principalActionProps: MprcButtonProps | null = null;

        if (enrollment.finalizationDate === null) {

            if (enrollment.canFinish) {
                actions.push(<EnrollmentCloseButton disabled={loading} />);
            }
        }
        else {
            const hasCertificate = enrollment.hasCertificate && enrollment.certificateId != null;
            const canReenroll = enrollment.canReEnroll;

            if (hasCertificate) {
                principalActionProps = {
                    text: t("Student.EnrollmentView.CertificateButtonText"),
                    onClick: () => getEnrollmentCertificate(enrollment.certificateId!)
                };
            } else if (canReenroll) {
                principalActionProps = {
                    text: t("Student.EnrollmentView.ReEnrollButtonText"),
                    onClick: () => setDialog(<ReEnrollAction onClose={() => setDialog(null)} />)
                };
            }
        }

        if (principalActionProps != null) {
            actions.push(
                <MprcButton
                    key="principalAction"
                    fullWidth
                    variant="contained"
                    title={principalActionProps.title ?? principalActionProps.text}
                    {...principalActionProps} />
            );
        }

        return (
            <>
                {
                    actions.length > 0 &&
                    <Grid sx={{ width: 220 }}>
                        <Stack direction="row" spacing={1}>
                            {actions}
                        </Stack>
                    </Grid>
                }
            </>
        );
    }, [enrollment, t, loading]);

    ///
    const RenderMinimezedActions = useMemo(() => {
        var actionProps: MprcButtonProps | null = null;

        if (enrollment.finalizationDate === null) {
            if (enrollment.canFinish && !loading) {
                actionProps = {
                    text: t("Student.EnrollmentView.CompleteButtonText"),
                    onClick: () => setClosing(true),
                    color: enrollment.canForceFinish ? "error" : "success"
                };
            }
            else if (hasNext()) {
                actionProps = {
                    text: t("Student.EnrollmentView.NextModuleButtonText"),
                    onClick: onNextModule
                };
            }
        }
        else {
            if (enrollment.hasCertificate && enrollment.certificateId != null) {
                actionProps = {
                    text: t("Student.EnrollmentView.CertificateButtonText"),
                    onClick: () => getEnrollmentCertificate(enrollment.certificateId!)
                };
            } else if (enrollment.canReEnroll) {
                actionProps = {
                    text: t("Student.EnrollmentView.ReEnrollButtonText"),
                    onClick: () => setDialog(<ReEnrollAction onClose={() => setDialog(null)} />)
                };
            }
        }
        return actionProps != null && <MprcButton sx={{ p: 0 }} size="small" variant="text" title={actionProps.title ?? actionProps.text} {...actionProps} />;
    }, [enrollment, hasNext, t, loading]);

    ///
    const MinimizedContent = useMemo(() => (
        <Grid container justifyContent="space-between" alignItems="center" direction="row" width="100%">
            <Grid size="grow">
                <Stack direction="row" alignItems="center" gap={8}>
                    {
                        (enrollment.status == EnrollStatus.NotStarted || enrollment.status == EnrollStatus.InProgress)
                            ? (
                                <>
                                    <Box>
                                        {
                                            enrollment.dueDate == null
                                                ? <Typography variant="caption" component="span" color="muted" textTransform="uppercase">{t("Student.EnrollmentView.WithoutDueDateText")}</Typography>
                                                : (
                                                    <>
                                                        <Typography variant="caption" component="span" textTransform="uppercase">
                                                            {t("Student.EnrollmentView.DueDateText")}&nbsp;
                                                        </Typography>
                                                        <Typography variant="caption" fontWeight="bold" component="span">
                                                            <MprcJoinComponents
                                                                separatorVariant="space"
                                                                components={[
                                                                    (
                                                                        <MprcText color={"warning"}>
                                                                            {Formaters.performaDate(enrollment.dueDate, { dayOfWeek: false, preposition: "none" })}
                                                                        </MprcText>
                                                                    )
                                                                    , (
                                                                        enrollment.isDelayed &&
                                                                        <MprcText color={"warning"} reverse>
                                                                            {t("Student.EnrollmentView.DueDateOverdueText")}
                                                                        </MprcText>
                                                                    )
                                                                    , (
                                                                        enrollment.isExpired &&
                                                                        <MprcText color={"error"} reverse>
                                                                            {t("Student.EnrollmentView.ExpiredText")}
                                                                        </MprcText>
                                                                    )
                                                                ]}
                                                            />
                                                        </Typography>
                                                    </>
                                                )
                                        }
                                    </Box>
                                    {
                                        !enrollment.course!.singleComponent && !enrollment.isPreview &&
                                        <ProgressResult
                                            typographyVariant="caption"
                                            variant={"inline"}
                                            data={{
                                                enrollmentId: enrollment.enrollmentId,
                                                progress: enrollment.progressPercent,
                                                minProgress: enrollment.minProgress ?? 0,
                                                status: enrollment.status,
                                                isClosed: enrollment.finalizationDate != null,
                                                totalComponents: enrollment.totalComputeProgressComponents,
                                                totalCompletedComponents: enrollment.totalCompletedComputeProgressComponents,
                                            }}
                                            enableShowDetails
                                        />
                                    }

                                    {!enrollment.course!.singleComponent && enrollment.hasScore &&
                                        <Typography variant="caption" component="span" textTransform="uppercase">
                                            {enrollment.totalCompletedScoredComponents == 0 ?
                                                t("Student.EnrollmentView.ScoreText")
                                                : enrollment.finalizationDate != null
                                                    ? t("Student.EnrollmentView.FinalScoreText")
                                                    : t("Student.EnrollmentView.PartialScoreText")
                                            }
                                            : <MprcText color="primary">
                                                <strong>{enrollment.totalCompletedScoredComponents == 0 ? '--' : `${enrollment.scorePercent ?? 0}%`}</strong>
                                            </MprcText>
                                        </Typography>
                                    }
                                </>
                            )
                            : (
                                <Grid>
                                    <EnrollmentStatus variant="inline" data={enrollment} />
                                </Grid>
                            )
                    }
                </Stack>
            </Grid>
            <Grid>
                {RenderMinimezedActions}
            </Grid>
        </Grid>
    ), [enrollment, hasNext, t, loading]);

    ///
    const RenderTitle = () => {

        return (
            <Grid container justifyContent="space-between" alignItems="center" direction="row" wrap="nowrap">
                <Grid size="grow">
                    <MprcTypography variant="h5" color="muted" component="div" noWrap>{enrollment.trainingName}</MprcTypography>
                    {expanded == true && enrollment.multipleClasses && <Typography variant="caption" color="muted" component="p" noWrap>{enrollment.classroomName}</Typography>}
                </Grid>
                <Grid>
                    <IconButton onClick={() => setExpanded(!expanded)} sx={{ p: 0.1 }}>
                        <FontAwesomeIcon icon={expanded ? faSquareCaretUp : faSquareCaretDown} color="muted.main" />
                    </IconButton>
                </Grid>
            </Grid>
        );
    };

    ///
    const RenderContent = () => {


        if (enrollment.course!.components.length == 1) {
            var currentComponent = enrollment.course!.components[0];
            if (currentComponent?.activityComponent && currentComponent?.componentType == TrainingComponentType.Activity)
                return <ActivityHeaderContent activity={currentComponent.activityComponent} />;
        }

        return (
            <Grid container spacing={2} width="100%">
                <Grid size={{ xs: 12 }} mt={1}>
                    <Grid container spacing={2} wrap="nowrap" width="100%">
                        <Grid>
                            <TrainingAvatar />
                        </Grid>
                        <Grid size="grow">
                            <MprcContainer>
                                {
                                    enrollment.status == EnrollStatus.NotStarted || enrollment.status == EnrollStatus.InProgress
                                        ? <TrainingDueDate enrollment={enrollment} />
                                        : <EnrollmentStatus variant="block" data={enrollment} />
                                }
                            </MprcContainer>
                            {!enrollment.isPreview &&
                                <MprcContainer>
                                    <Grid container spacing={8} width="100%">
                                        {
                                            !enrollment.course!.singleComponent &&
                                            <Grid>
                                                <ProgressResult
                                                    variant={"block"}
                                                    data={{
                                                        enrollmentId: enrollment.enrollmentId,
                                                        progress: enrollment.progressPercent,
                                                        minProgress: enrollment.minProgress ?? 0,
                                                        status: enrollment.status,
                                                        isClosed: enrollment.finalizationDate != null,
                                                        totalComponents: enrollment.totalComputeProgressComponents,
                                                        totalCompletedComponents: enrollment.totalCompletedComputeProgressComponents,
                                                    }}
                                                    enableShowDetails
                                                />
                                            </Grid>
                                        }
                                        {
                                            enrollment.hasScore &&
                                            <Grid>
                                                <ScoreResult
                                                    variant={enrollment.course!.singleComponent ? "single" : "multiple"}
                                                    enrollmentId={enrollment.enrollmentId}
                                                    score={enrollment.scorePercent}
                                                    minScore={enrollment.minFinalScore ?? 0}
                                                    isClosed={enrollment.finalizationDate != null}
                                                    totalItems={enrollment.totalScoredComponents}
                                                    totalCompletedItems={enrollment.totalCompletedScoredComponents}
                                                    status={enrollment.status}
                                                    enableShowDetails
                                                />
                                            </Grid>
                                        }
                                    </Grid>
                                </MprcContainer>
                            }
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2} width="100%">
                        {RenderActions}
                        <Grid size="grow">
                            <TrainingInteractions enrollment={enrollment} readOnly={enrollment.isPreview} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    //////////////////////////////////////////////////////
    /// RENDER
    //////////////////////////////////////////////////////

    return (
        <Box sx={theme => ({
            backgroundColor: theme.palette.common.white,
            marginLeft: theme.spacing(-2),
            marginRight: theme.spacing(-2),
            borderBottom: "solid 1px",
            borderBottomColor: theme.extended.workArea.navigationBar.separatorColor,
        })}>
            {dialog}
            <MprcContainer margin="none">
                <Box>
                    <Stack direction="row" gap={1} alignItems="center" p={1}>
                        {!expanded && <TrainingAvatar size="extra-small" />}
                        <Grid container direction="row" width="100%">
                            <Grid size={{ xs: 12 }}>
                                {RenderTitle()}
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Collapse in={expanded ?? true} unmountOnExit>
                                    {RenderContent()}
                                </Collapse>
                                {expanded == false && MinimizedContent}
                            </Grid>
                        </Grid>
                    </Stack>
                </Box>
            </MprcContainer>

            <DoCloseEnrollment
                open={closing}
                onAction={
                    (status) => {
                        setClosing(false);
                        if (status == "closed") onReload();
                    }
                }
            />
        </Box>
    );
};
